import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateCreationMetadata } from "../../../store/slices/projectSlice";
import { ChevronDown, ChevronUp, X, Settings } from "lucide-react";
import { renderGrid } from "../../common/FormRenderer";
import UserLookupField from "../../common/UserLookupField";
import { Modal, Button } from "antd";
import {
  allSections,
  PICKLIST_LABELS,
} from "../../../config/projectMetadataFields";
import { validateProjectEditForm } from "../../../config/projectEdit.validation";
import {
  usePicklistMap,
  resolvePicklistFields,
} from "../../../hooks/usePicklist";

const ProjectMetadata = () => {
  const dispatch = useDispatch();
  const { metadata, requirements } = useSelector(
    (state) => state.project.creation,
  );

  const { picklistMap } = usePicklistMap(Object.values(PICKLIST_LABELS));

  const [expandedSections, setExpandedSections] = useState({
    information: true,
    requirements: false,
    people: false,
    rates: false,
    funnelTotals: false,
    funnelStages: false,
    paymentConfigurations: false,
    activation: false,
    leverRequisitionActions: false,
  });

  const [showValidation, setShowValidation] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [advancedExpandedSections, setAdvancedExpandedSections] = useState({
    rates: true,
    funnelTotals: false,
    funnelStages: false,
    paymentConfigurations: false,
    activation: false,
    leverRequisitionActions: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleAdvancedSection = (section) => {
    setAdvancedExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Define which sections are "advanced"
  const advancedSectionKeys = [
    "rates",
    "funnelTotals",
    "funnelStages",
    "activation",
    "leverRequisitionActions",
  ];

  // Resolve picklist options into field configs from the describe API
  const resolvedSections = useMemo(
    () =>
      allSections.map((section) => ({
        ...section,
        fields: resolvePicklistFields(section.fields, picklistMap),
      })),
    [picklistMap],
  );

  // Filter sections into basic and advanced
  const basicSections = resolvedSections.filter(
    (s) => !advancedSectionKeys.includes(s.key),
  );
  const advancedSections = resolvedSections.filter((s) =>
    advancedSectionKeys.includes(s.key),
  );

  const validateRequiredFields = () => {
    // Use centralized validation
    const validationPayload = {
      metadata: {
        name: metadata?.name,
        shortName: metadata?.shortName,
        appenPartner: metadata?.appenPartner,
        account: metadata?.account,
        hireStartDate: metadata?.hireStartDate,
        predictedCloseDate: metadata?.predictedCloseDate,
        projectStatus: metadata?.projectStatus,
        projectType: metadata?.projectType,
        projectPriority: metadata?.projectPriority,
        projectManager: metadata?.projectManager,
        projectPaymentMethod: metadata?.projectPaymentMethod,
        shortDescription: metadata?.shortDescription,
      },
      objectives: {
        contributorFacingName:
          metadata?.contributorProjectName || metadata?.contributorFacingName,
      },
    };

    const validationErrors = validateProjectEditForm(validationPayload);

    // Convert centralized validation errors to readable messages
    const missing = [];
    Object.entries(validationErrors).forEach(([path, message]) => {
      const fieldNameMap = {
        "metadata.name": "Project Name",
        "metadata.shortName": "Short Project Name",
        "metadata.appenPartner": "Appen Partner",
        "metadata.account": "Account",
        "metadata.hireStartDate": "Hire Start Date",
        "metadata.predictedCloseDate": "Predicted Close Date",
        "metadata.projectStatus": "Project Status",
        "metadata.projectType": "Project Type",
        "metadata.projectPriority": "Project Priority",
        "metadata.projectManager": "Project Manager",
        "metadata.projectPaymentMethod": "Project Payment Method",
        "metadata.shortDescription": "Short Description",
        "objectives.contributorFacingName": "Contributor Project Name",
      };
      const fieldLabel = fieldNameMap[path] || path;
      missing.push(`${fieldLabel}: ${message}`);
    });

    setMissingFields(missing);
    setShowValidation(true);
    return missing.length === 0;
  };

  const isFieldInvalid = (fieldName) => {
    if (!showValidation) return false;
    const value = metadata?.[fieldName];
    return !value || String(value).trim() === "";
  };

  const handleInputChange = (field, value, displayName) => {
    const update = { [field]: value };
    if (displayName !== undefined) {
      update[`${field}DisplayName`] = displayName;
    }
    dispatch(updateCreationMetadata(update));
  };

  const SectionHeader = ({ title, section }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between mt-6 mb-3 pb-2 border-b border-gray-200 hover:bg-gray-50 px-2 -mx-2 rounded transition-colors"
    >
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-gray-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );

  // Render People section with UserLookupField and checkboxes
  const renderPeopleSection = (section) => {
    if (!expandedSections[section.key]) return null;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {section.fields.map((field) => {
            // Handle checkbox fields in People section
            if (field.type === "checkbox") {
              return (
                <div key={field.field} className="flex items-center">
                  <input
                    type="checkbox"
                    id={field.field}
                    checked={metadata[field.field] || false}
                    onChange={(e) =>
                      handleInputChange(field.field, e.target.checked)
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor={field.field}
                    className="ml-2 block text-sm text-gray-900"
                  >
                    {field.label}
                  </label>
                </div>
              );
            }

            // Handle user lookup fields
            return (
              <UserLookupField
                key={field.field}
                label={field.label}
                value={metadata[field.field] || ""}
                onChange={(email, userId) => {
                  handleInputChange(field.field, email);
                  handleInputChange(`${field.field}Id`, userId);
                }}
                placeholder="Enter email address"
                required={field.required}
                showError={
                  showValidation ||
                  (field.required && isFieldInvalid(field.field))
                }
                searchable={field.searchable || false}
              />
            );
          })}
        </div>
      </div>
    );
  };

  // Render regular sections with FormRenderer
  const renderSection = (section) => {
    if (!expandedSections[section.key]) return null;

    // Special handling for People section
    if (section.specialComponent === "UserLookupField") {
      return renderPeopleSection(section);
    }

    // Regular sections
    return renderGrid(section.fields, {
      formData: { ...metadata, ...requirements },
      onChange: handleInputChange,
      showValidation,
    });
  };

  return (
    <div className="fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Project Metadata
        </h2>
        <p className="text-gray-600">
          Provide basic details about your project
        </p>
      </div>

      {/* Validation Error Banner */}
      {showValidation && missingFields.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 mb-2">
                Please complete the following required fields:
              </h3>
              <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                {missingFields.map((field, index) => (
                  <li key={index}>{field}</li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => setShowValidation(false)}
              className="flex-shrink-0 text-red-400 hover:text-red-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Render basic sections */}
      <div className="space-y-4">
        {basicSections.map((section) => (
          <div key={section.key}>
            <SectionHeader title={section.section} section={section.key} />
            {renderSection(section)}
          </div>
        ))}
      </div>

      {/* Advanced Fields Button */}
      <div className="mt-8">
        <button
          type="button"
          onClick={() => setShowAdvancedModal(true)}
          className="w-full px-4 py-3 border-2 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-md"
          style={{
            borderColor: "#08979c",
            color: "#08979c",
            backgroundColor: "white",
          }}
        >
          <Settings className="w-5 h-5" />
          <span className="font-medium">Advanced Fields</span>
          <span className="text-sm text-gray-500">(Optional)</span>
        </button>
      </div>

      {/* Advanced Fields Modal */}
      <Modal
        title="Advanced Fields"
        open={showAdvancedModal}
        onCancel={() => setShowAdvancedModal(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setShowAdvancedModal(false)}
          >
            Done
          </Button>,
        ]}
        width={900}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <div className="space-y-3">
          {advancedSections.map((section) => {
            const AdvancedSectionHeader = ({ title, sectionKey }) => (
              <button
                type="button"
                onClick={() => toggleAdvancedSection(sectionKey)}
                className="w-full flex items-center justify-between p-2.5 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
              >
                <span className="text-sm font-semibold text-gray-700">
                  {title}
                </span>
                {advancedExpandedSections[sectionKey] ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>
            );

            return (
              <div key={section.key}>
                <AdvancedSectionHeader
                  title={section.section}
                  sectionKey={section.key}
                />
                {advancedExpandedSections[section.key] && (
                  <div className="mt-2">
                    {renderGrid(section.fields, {
                      formData: metadata,
                      onChange: handleInputChange,
                      showValidation,
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
};

export default ProjectMetadata;
